import type { PriceBookItem, PriceCode } from "@/hooks/api/use-campaign-requests";

import { MARKETS } from "../constants";

/**
 * The price book, resolved the way the API resolves it.
 *
 * Everything in this file is a mirror of `src/modules/campaign-requests/services/price-book.service.ts`
 * — {@link isPlaceholderRate} copies the note marker and {@link resolveRate} copies the
 * most-specific-first lookup. A mirror that drifts is worse than no mirror: it would show the
 * admin one number and quote the client another, which is exactly the failure the price book
 * exists to prevent. Change these only when the server file changes, and change them to match.
 */

/**
 * The marker the seed migration writes on every rate nobody has agreed yet.
 *
 * Matched as a prefix, exactly as the server matches it, so "placeholder rate - waiting on
 * Telnyx" still reads as unconfirmed here.
 */
export const PLACEHOLDER_PREFIX = "placeholder rate";

/** What the seed writes on the one rate that *is* agreed, and what "Confirm" writes back. */
export const CONFIRMED_NOTE = "confirmed with the client";

export function isPlaceholderRate(note?: string | null): boolean {
	return (note ?? "").trim().toLowerCase().startsWith(PLACEHOLDER_PREFIX);
}

/**
 * Every opening a seeded rate has been written with, in any database this build meets.
 *
 * {@link isPlaceholderRate} is a mirror of the server and answers exactly one question — will
 * `priceRequest()` refuse to quote against this rate. It is not the same question as "has anyone
 * agreed this number", and the two have already come apart: rows seeded before the marker settled
 * carry "illustrative local-test rate - NOT agreed with any client", which says plainly that
 * nobody agreed it and does not start with the prefix the server matches on.
 *
 * Presentation reads the wider list so an unagreed rate is never drawn as a confirmed one. The
 * narrow, server-matching test stays the only thing allowed to claim a rate blocks quoting.
 */
const UNAGREED_PREFIXES = [PLACEHOLDER_PREFIX, "illustrative local-test rate"];

/** The note says, in one wording or another, that nobody has agreed this number. */
export function isUnagreedRate(note?: string | null): boolean {
	const text = (note ?? "").trim().toLowerCase();
	return UNAGREED_PREFIXES.some((prefix) => text.startsWith(prefix));
}

/**
 * Boilerplate the badges already say, longest first.
 *
 * The whole seeded sentences are here as well as the two markers they start with, and the order
 * matters: matching "illustrative local-test rate" alone would strip the marker and leave "NOT
 * agreed with any client" in the column — the same sentence, ten times, minus four words. Longest
 * wins, so a seeded note collapses to nothing and "placeholder rate - waiting on Telnyx" still
 * keeps the half somebody typed.
 */
const BOILERPLATE_PREFIXES = [
	"illustrative local-test rate - not agreed with any client",
	"placeholder rate - confirm before quoting a client",
	...UNAGREED_PREFIXES,
	CONFIRMED_NOTE,
].toSorted((left, right) => right.length - left.length);

/**
 * What a note says that its badge does not.
 *
 * The seed writes the same sentence onto every unconfirmed rate, so the Note column was ten
 * identical two-line paragraphs — clutter, and seed language reading as a live rate card. The
 * sentence is not deleted (the stored note is what the server matches on, and confirming a rate is
 * what rewrites it); it is said once above the table as a legend, and the column keeps only what
 * somebody actually wrote — the "waiting on Telnyx" appended to a marker, or a note of their own.
 */
export function noteDetail(note?: string | null): string {
	const text = (note ?? "").trim();
	if (text.length === 0) return "";

	const lower = text.toLowerCase();
	const prefix = BOILERPLATE_PREFIXES.find((candidate) => lower.startsWith(candidate));
	if (prefix === undefined) return text;

	// Whatever separates the marker from the appended context, so "…rate - waiting on Telnyx"
	// leaves "waiting on Telnyx" rather than "- waiting on Telnyx".
	return text
		.slice(prefix.length)
		.replace(/^[\s,:;·–—-]+/, "")
		.trim();
}

/**
 * The codes `priceRequest()` actually resolves, and therefore the only ones whose absence stops a
 * quote. Carrier is here too but lives in its own table below — it is the one code that genuinely
 * differs by destination, which is why it is priced per market rather than blended into call time.
 */
export const QUOTED_CHARGE_CODES: PriceCode[] = [
	"agent_setup",
	"translated_agent",
	"regional_deployment",
	"agent_revision",
	"number_rental",
	"call_time",
];

export const CARRIER_CODE: PriceCode = "carrier_rate";

/**
 * Configurable, but never resolved by the pricer. A missing one of these is not a gap — nagging
 * about it would train the operator to ignore the amber that does matter.
 */
export const OPTIONAL_CHARGE_CODES: PriceCode[] = ["minimum_charge"];

/**
 * `reuse_existing` is deliberately absent from every list here: reuse is free by decision, not by
 * rate. The pricer never looks it up, so a row for it in the price book would be a number that
 * cannot affect anything.
 */
export const EDITABLE_CODES: PriceCode[] = [...QUOTED_CHARGE_CODES, CARRIER_CODE, ...OPTIONAL_CHARGE_CODES];

const CHARGE_CODE_ORDER: PriceCode[] = [...QUOTED_CHARGE_CODES, ...OPTIONAL_CHARGE_CODES];

/** Where the amount on a row came from, which is the whole point of the account view. */
export type RateSource =
	/** The account's own rate for exactly this market. */
	| "override"
	/** The account's all-markets rate, standing in for this market. */
	| "override_all_markets"
	/** The platform default for exactly this market — the account has not negotiated it. */
	| "platform"
	/** The platform all-markets default, standing in for this market. */
	| "platform_all_markets"
	/** Nothing is configured at any level. */
	| "unset";

export interface ResolvedRate {
	item: PriceBookItem | null;
	source: RateSource;
}

/**
 * The rate that applies, resolved most-specific-first:
 *   account + market → account → platform + market → platform
 *
 * Returns `unset` rather than falling back to zero, because that is what the server does: a
 * missing rate is a gap somebody has to answer for, and quietly charging nothing is how an
 * unpriced charge reaches a client.
 */
export function resolveRate(
	items: PriceBookItem[],
	code: PriceCode,
	accountId: string | null,
	market: string | null = null
): ResolvedRate {
	const candidates = items.filter((item) => item.code === code);
	const forAccount = accountId ? candidates.filter((item) => item.accountId === accountId) : [];
	const forPlatform = candidates.filter((item) => item.accountId === null);

	const accountExact = market ? forAccount.find((item) => item.market === market) : undefined;
	const accountAny = forAccount.find((item) => item.market === null);
	const platformExact = market ? forPlatform.find((item) => item.market === market) : undefined;
	const platformAny = forPlatform.find((item) => item.market === null);

	if (accountExact) return { item: accountExact, source: "override" };
	if (accountAny) return { item: accountAny, source: market ? "override_all_markets" : "override" };
	if (platformExact) return { item: platformExact, source: "platform" };
	if (platformAny) return { item: platformAny, source: market ? "platform_all_markets" : "platform" };

	return { item: null, source: "unset" };
}

export interface RateRow {
	/** Stable across a refetch: the row *is* its (code, market) pair, as it is on the server. */
	key: string;
	code: PriceCode;
	market: string | null;
	/** The platform default configured for exactly this pair, if there is one. */
	platform: PriceBookItem | null;
	/** The selected account's own rate for exactly this pair, if it has negotiated one. */
	override: PriceBookItem | null;
	/** What the API would resolve for the current view — the number that would reach a quote. */
	effective: PriceBookItem | null;
	source: RateSource;
	/** The effective rate still carries the seed's note: the API refuses to quote against it. */
	placeholder: boolean;
	/**
	 * Nobody has agreed the effective amount, in any of the wordings a seed has used. Always true
	 * where `placeholder` is, and true as well of the older seed note the server does not match —
	 * which is a rate that is quotable and still nobody's agreed price.
	 */
	unconfirmed: boolean;
	/** Nothing resolves and the pricer needs this code: quoting refuses with "no rate configured". */
	missing: boolean;
	/** The part of the note nobody's badge already says. Empty for a rate carrying only boilerplate. */
	note: string;
}

function rowKey(code: PriceCode, market: string | null): string {
	return `${code}::${market ?? "all"}`;
}

function buildRow(items: PriceBookItem[], code: PriceCode, market: string | null, accountId: string | null): RateRow {
	const { item, source } = resolveRate(items, code, accountId, market);

	return {
		key: rowKey(code, market),
		code,
		market,
		platform: items.find((row) => row.accountId === null && row.code === code && row.market === market) ?? null,
		override: accountId
			? (items.find((row) => row.accountId === accountId && row.code === code && row.market === market) ?? null)
			: null,
		effective: item,
		source,
		placeholder: isPlaceholderRate(item?.note),
		unconfirmed: isUnagreedRate(item?.note),
		missing: source === "unset" && (QUOTED_CHARGE_CODES.includes(code) || code === CARRIER_CODE),
		note: noteDetail(item?.note),
	};
}

/**
 * One row per (code, market) worth showing — which includes the ones that do not exist yet.
 *
 * A rate nobody has set is the failure the price book is meant to surface, so an unconfigured
 * quoted charge gets a row of its own rather than being left out of the table. "Not set" is a
 * state the UI nags about; a blank space is one nobody sees.
 */
export function buildChargeRows(items: PriceBookItem[], accountId: string | null): RateRow[] {
	const pairs = new Map<string, { code: PriceCode; market: string | null }>();

	for (const code of CHARGE_CODE_ORDER) pairs.set(rowKey(code, null), { code, market: null });
	for (const item of items) {
		if (item.code === CARRIER_CODE) continue;
		pairs.set(rowKey(item.code, item.market), { code: item.code, market: item.market });
	}

	return [...pairs.values()]
		.map((pair) => buildRow(items, pair.code, pair.market, accountId))
		.toSorted((left, right) => {
			const byCode = CHARGE_CODE_ORDER.indexOf(left.code) - CHARGE_CODE_ORDER.indexOf(right.code);
			if (byCode !== 0) return byCode;
			return (left.market ?? "").localeCompare(right.market ?? "");
		});
}

/**
 * Carrier termination, one row per market we dial. Thailand runs roughly double Singapore, which
 * is why this is a per-market table rather than one blended number.
 */
export function buildCarrierRows(items: PriceBookItem[], accountId: string | null): RateRow[] {
	const markets = new Set<string | null>(MARKETS.map((market) => market.value));
	for (const item of items) {
		if (item.code === CARRIER_CODE) markets.add(item.market);
	}

	return [...markets]
		.map((market) => buildRow(items, CARRIER_CODE, market, accountId))
		.toSorted((left, right) => (left.market ?? "").localeCompare(right.market ?? ""));
}

export interface RateSummary {
	/** Rates the server itself refuses to quote against. A subset of `unconfirmed`. */
	placeholders: number;
	/** Rates nobody has agreed, however the note says so. What the legend above the table counts. */
	unconfirmed: number;
	missing: number;
	overrides: number;
}

/** The counts the banner reads from. Placeholders and gaps both stop a quote, and are counted apart. */
export function summariseRates(rows: RateRow[]): RateSummary {
	return rows.reduce<RateSummary>(
		(summary, row) => ({
			placeholders: summary.placeholders + (row.placeholder ? 1 : 0),
			unconfirmed: summary.unconfirmed + (row.unconfirmed ? 1 : 0),
			missing: summary.missing + (row.missing ? 1 : 0),
			overrides: summary.overrides + (row.override ? 1 : 0),
		}),
		{ placeholders: 0, unconfirmed: 0, missing: 0, overrides: 0 }
	);
}

/**
 * Money for a rate table, which is not the same job as money on a quote.
 *
 * A quote line rounds to two decimals, which is right there and
 * wrong here: the column is numeric(12,4) and a carrier rate of 0.0120 per minute would render as
 * S$0.01, a 20% lie on the one number the margin depends on. Coerced once, here, at the edge —
 * the API sends decimal strings.
 */
export function formatRate(value: string | number | null | undefined, currency = "SGD"): string {
	if (value === null || value === undefined || value === "") return "—";

	const amount = Number(value);
	if (!Number.isFinite(amount)) return "—";

	try {
		return new Intl.NumberFormat("en-SG", {
			style: "currency",
			currency,
			// The code rather than the symbol, because the Currency column that used to carry it is
			// gone — it was a second column repeating what this one already had, and "$" alone does
			// not distinguish SGD from the half-dozen other currencies that use it.
			currencyDisplay: "code",
			minimumFractionDigits: 2,
			maximumFractionDigits: 4,
		}).format(amount);
	} catch {
		// An unknown or malformed currency code must not blank out the whole table.
		return `${currency} ${amount.toFixed(4)}`;
	}
}
